import { EventQueue } from './EventQueue';
import { SESSION_STORAGE_KEY } from './constants';
import {
  formatEventTime,
  generateSessionId,
  isTrackDebugEnabled,
} from './utils';

/**
 * 埋点核心类：采集、补全、上报用户操作事件
 */
class Tracker {
  constructor() {
    this.queue = new EventQueue();
    this.router = null;
    this.sessionId = '';
    this.initialized = false;
    this.boundBeforeUnload = null;
    this.boundVisibilityChange = null;
  }

  /**
   * 初始化 Tracker
   */
  init(options = {}) {
    if (options.router) {
      this.router = options.router;
    }

    this.sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY) || '';
    if (!this.sessionId) {
      this.sessionId = generateSessionId();
      sessionStorage.setItem(SESSION_STORAGE_KEY, this.sessionId);
    }

    if (this.initialized) {
      return;
    }

    this.queue.start();
    this.bindLifecycleHandlers();
    this.initialized = true;

    if (isTrackDebugEnabled()) {
      console.log('[Tracker] init', this.sessionId);
    }
  }

  /**
   * 获取当前 sessionId
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * 是否已登录可上报
   */
  isEnabled() {
    return !!sessionStorage.getItem('authorization');
  }

  /**
   * 采集单条事件
   */
  track(payload) {
    if (!this.isEnabled()) {
      return;
    }

    if (!this.initialized) {
      this.init({ router: this.router || undefined });
    }

    const route = this.router && this.router.currentRoute;
    const event = {
      session_id: this.sessionId,
      event_type: payload.event_type,
      event_name: payload.event_name,
      page_path: payload.page_path || (route && route.fullPath) || window.location.hash.replace('#', '') || '/',
      page_title: payload.page_title || (route && route.name) || document.title || '',
      from_path: payload.from_path || '',
      referrer: payload.referrer || document.referrer || '',
      extra: payload.extra || {},
      event_time: payload.event_time || formatEventTime(),
    };

    this.queue.push(event);

    if (isTrackDebugEnabled()) {
      console.log('[Tracker] track', event);
    }
  }

  /**
   * 记录点击事件
   */
  trackClick(name, extra = {}) {
    this.track({
      event_type: 'click',
      event_name: name,
      extra,
    });
  }

  /**
   * 立即冲刷队列
   */
  async flush(useBeacon = false) {
    await this.queue.flush(useBeacon);
  }

  /**
   * 销毁 Tracker，登出时调用
   */
  async destroy() {
    if (!this.initialized) {
      return;
    }

    this.track({
      event_type: 'custom',
      event_name: 'system:logout',
      extra: { label: '用户登出' },
    });

    await this.queue.flush(false);
    this.unbindLifecycleHandlers();
    this.queue.stop();
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    this.sessionId = '';
    this.initialized = false;
  }

  /**
   * 绑定页面生命周期
   */
  bindLifecycleHandlers() {
    this.boundBeforeUnload = () => {
      this.queue.flush(true);
    };

    this.boundVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.queue.flush(true);
      }
    };

    window.addEventListener('beforeunload', this.boundBeforeUnload);
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
  }

  /**
   * 解绑页面生命周期
   */
  unbindLifecycleHandlers() {
    if (this.boundBeforeUnload) {
      window.removeEventListener('beforeunload', this.boundBeforeUnload);
      this.boundBeforeUnload = null;
    }

    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      this.boundVisibilityChange = null;
    }
  }
}

export const tracker = new Tracker();

export default tracker;
